import { useEffect, useCallback } from 'react'
import { loadMapState, saveMapState } from '../utils/mapValidation'

/**
 * Custom hook for managing map state persistence
 * Handles loading and saving map state to sessionStorage
 * 
 * @param {Object} params - Configuration parameters
 * @param {string} params.storageKey - Key for sessionStorage
 * @param {boolean} params.shouldPersist - Whether to persist state (false in entity mode)
 * @param {Object} params.stateToSave - Current state to persist
 * @param {Array} params.dependencies - Dependencies for save effect
 * @returns {Object} - { persistedState, saveState }
 */
export function useMapPersistence({
  storageKey,
  shouldPersist = true,
  stateToSave = {},
  dependencies = []
}) {
  // Load persisted state on mount
  const loadPersistedState = useCallback(() => {
    if (!shouldPersist) {
      return null
    }
    return loadMapState(storageKey)
  }, [storageKey, shouldPersist])

  const persistedState = loadPersistedState()

  // Save state to sessionStorage when dependencies change
  // Debounced to avoid excessive writes
  useEffect(() => {
    if (!shouldPersist) {
      return
    }

    const timer = setTimeout(() => {
      saveMapState(storageKey, stateToSave)
    }, 500) // Wait 500ms after last change before saving

    return () => clearTimeout(timer)
  }, [storageKey, shouldPersist, ...dependencies])

  return { persistedState }
}
